import { UIMetadataComplete } from '../../../types/UIMetadata';

/**
 * Equinox Configurator (linkId 2362).
 *
 * UI half of the 2.0 metadata pair — every key mirrors a configurationId in
 * ./graphics-metadata.ts. Converted from the 1.0 repo's
 * `src/metadata/data/metadata-2362.ts`; shared UI boilerplate
 * (staticTranslations / saveFormConfig) follows the 2378 project.
 *
 * Conversion notes:
 *  - 1.0 `removeAccessories` + `defaultAccessory` become a shared graphics
 *    `groupId` + `isDefault` (2.0's single-select mechanism), namespaced per
 *    trim because the SDK resolves groupIds model-wide.
 *  - 1.0 `forColor` (allow-list of colour indices) is inverted into 2.0's
 *    `notAvailableForColor` deny-list of colour ids.
 *  - `interactionApplyMessage`/`applyInteractions` become
 *    `affectedInteractionOnApply`; `includedPackages` become
 *    `dependencyPackages`; `brandItemCode` becomes `autoIntelRPOCode`.
 *  - Dropped, with no 2.0 equivalent: `video` Welcome Animation interactions,
 *    feature `tempSkuToLoad`/`replaceAsset`, `interactionRemoveMessage`,
 *    interaction `meshAnimation`/`glowMesh`.
 *  - Broken references in the 1.0 source (dangling ids, duplicate accessory
 *    ids, identifiers naming assets the variant never loads) were repaired —
 *    see conversion-report-2362.txt.
 *
 * Assets live in public/assets/{images,videos}/2362/.
 */
// Cast through unknown to allow additional model-level UI fields (e.g., name, specifications)
export const uimetadata = {
  models: {
    Equinox: {
      variants: {
        'equinox-ev-rs-fwd-2026': {
          configurationId: '5vlpab0glq63',
          name: {
            en: '2026 Equinox EV',
            ar: '2026 Equinox EV'
          },
          description: {
            en: 'RS, FWD',
            ar: 'RS، دفع أمامي'
          },
          drivetrain: 'RS, FWD',
          trim: 'Equinox EV',
          modelyear: '2026',
          image: 'images/2362/variant_2026.png',
          price: {
            KSA: 173132,
            UAE: 169900,
            OMR: 18006,
            BHD: 17544,
            QAR: 168053,
            JOD: 32780,
            KW: 14312,
            IQD: 46168,
            LBP: 46168,
            default: 169900,
          },
          basePrice: {
            UAE: 169900,
            KSA: 189600,
            KW: 13500,
            BHD: 17462,
            QAR: 190000,
            IQD: 60619096,
            LBP: 4142266378,
            default: 169900
          },
          index: 0,
          isDefault: true,
          ar: {
            enable: false,
            redirection: false
          },
          autoIntelRPOCode: 'CHEQ2026_01',
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          brandItemCode: 'CHEQ01',
        },
        'equinox-ev-rs-fwd-2025': {
          configurationId: '5vlpab0lpglq63',
          name: {
            en: '2025 Equinox EV',
            ar: '2025 Equinox EV'
          },
          description: {
            en: 'RS, FWD',
            ar: 'RS، دفع أمامي'
          },
          drivetrain: 'RS, FWD',
          trim: 'Equinox EV',
          modelyear: '2025',
          image: 'images/2362/variant_2025.png',
          price: {
            KSA: 173132,
            UAE: 169900,
            OMR: 18006,
            BHD: 17544,
            QAR: 168053,
            JOD: 32780,
            KW: 14312,
            IQD: 46168,
            LBP: 46168,
            default: 169900,
          },
          basePrice: {
            UAE: 169900,
            KSA: 189600,
            KW: 13500,
            BHD: 17462,
            QAR: 190000,
            IQD: 60619096,
            LBP: 4142266378,
            default: 169900
          },
          index: 1,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          autoIntelRPOCode: 'CHEQUC223_01',
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          brandItemCode: 'CHEQ01',
        }
      },
      backgrounds: {
        g0ZA968_RpUd: {
          image: 'images/2362/change_bg_3.webp',
          title: {
            en: 'City Landscape 1',
            ar: 'غروب الشمس على الشاطئ'
          }
        },
        g0ZA968_RpUe: {
          image: 'images/2362/change_bg_2.webp',
          title: {
            en: 'City Landscape 2',
            ar: 'غروب الشمس على الشاطئ'
          }
        }
      },
      ambientLighting: {},
      colors: {
        interior: {
          '48za413x8262': {
            label: {
              en: 'Black/ Adrenaline Red Interior',
              ar: 'جلد Evotex باللون الأحمر'
            },
            image: 'images/2362/interior_red.png',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              IQD: 0,
              LBP: 0,
              default: 0,
            },
            priceBreakdown: [],
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'IN01'
          }
        },
        exterior: {
          '2fa06p9t7v8f': {
            label: {
              en: 'Black',
              ar: 'أسود'
            },
            image: 'images/2362/ext_colors/official/Black.jpg',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              IQD: 0,
              LBP: 0,
              default: 0,
            },
            priceBreakdown: [],
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2362/ext_colors/official/Black.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'EX02',
            brandItemCode: 'EX02',
          },
          '69xux2hlscm1': {
            label: {
              en: 'Sterling Gray Metallic',
              ar: 'رمادي معدني-1'
            },
            image: 'images/2362/ext_colors/official/Sterling-Gray-Metallic.jpg',
            usdz: 'silver',
            price: {
              KSA: 895,
              UAE: 878,
              OMR: 93,
              BHD: 91,
              QAR: 868,
              JOD: 169,
              KW: 74,
              IQD: 239,
              LBP: 239,
              default: 878,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Sterling Gray',
                  ar: 'ستيرلنج جراي'
                },
                price: {
                  KSA: 895,
                  UAE: 878,
                  OMR: 93,
                  BHD: 91,
                  QAR: 868,
                  JOD: 169,
                  KW: 74,
                  IQD: 239,
                  LBP: 239,
                  default: 878,
                }
              }
            ],
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2362/ext_colors/official/Sterling-Gray-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'EX13',
            brandItemCode: 'EX05',
          },
          '5xzh26yrdinw': {
            label: {
              en: 'Radiant Red Tintcoat',
              ar: 'أحمر داكن معدني'
            },
            image: 'images/2362/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            usdz: 'red',
            price: {
              KSA: 2156,
              UAE: 2116,
              OMR: 224,
              BHD: 218,
              QAR: 2093,
              JOD: 408,
              KW: 178,
              IQD: 575,
              LBP: 575,
              default: 2116,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Radiant Red',
                  ar: 'أحمر مشع'
                },
                price: {
                  KSA: 2156,
                  UAE: 2116,
                  OMR: 224,
                  BHD: 218,
                  QAR: 2093,
                  JOD: 408,
                  KW: 178,
                  IQD: 575,
                  LBP: 575,
                  default: 2116,
                }
              }
            ],
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2362/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'EX03',
            brandItemCode: 'EX03',
          },
          '2fbmj48vpgsi': {
            label: {
              en: 'Summit White',
              ar: 'القمة البيضاء'
            },
            image: 'images/2362/ext_colors/official/Summit-White.jpg',
            usdz: 'white',
            price: {
              KSA: 3728,
              UAE: 3658,
              OMR: 388,
              BHD: 378,
              QAR: 3618,
              JOD: 706,
              KW: 308,
              IQD: 994,
              LBP: 994,
              default: 3658,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Summit White',
                  ar: 'القمة البيضاء'
                },
                price: {
                  KSA: 3728,
                  UAE: 3658,
                  OMR: 388,
                  BHD: 378,
                  QAR: 3618,
                  JOD: 706,
                  KW: 308,
                  IQD: 994,
                  LBP: 994,
                  default: 3658,
                }
              }
            ],
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2362/ext_colors/official/Summit-White.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'EX01',
            brandItemCode: 'EX01',
          },
          '2fbmj48vpgsi-my25': {
            label: {
              en: 'Summit White',
              ar: 'القمة البيضاء'
            },
            image: 'images/2362/ext_colors/official/Summit-White.jpg',
            usdz: 'white',
            price: {
              KSA: 3728,
              UAE: 3658,
              OMR: 388,
              BHD: 378,
              QAR: 3618,
              JOD: 706,
              KW: 308,
              IQD: 994,
              LBP: 994,
              default: 3658,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Summit White',
                  ar: 'القمة البيضاء'
                },
                price: {
                  KSA: 3728,
                  UAE: 3658,
                  OMR: 388,
                  BHD: 378,
                  QAR: 3618,
                  JOD: 706,
                  KW: 308,
                  IQD: 994,
                  LBP: 994,
                  default: 3658,
                }
              }
            ],
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2362/ext_colors/official/Summit-White.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'EX01',
            brandItemCode: 'EX01',
          },
          '10xy9i682nxc': {
            label: {
              en: 'Riptide Blue Metallic',
              ar: 'الأزرق المعدني'
            },
            image: 'images/2362/ext_colors/official/Riptide-Blue-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 895,
              UAE: 878,
              OMR: 93,
              BHD: 91,
              QAR: 868,
              JOD: 169,
              KW: 74,
              IQD: 239,
              LBP: 239,
              default: 878,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Riptide Blue Metallic',
                  ar: 'الأزرق المعدني'
                },
                price: {
                  KSA: 895,
                  UAE: 878,
                  OMR: 93,
                  BHD: 91,
                  QAR: 868,
                  JOD: 169,
                  KW: 74,
                  IQD: 239,
                  LBP: 239,
                  default: 878,
                }
              }
            ],
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2362/ext_colors/official/Riptide-Blue-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'EX04',
            brandItemCode: 'EX04',
          },
          '37xytt0490j8': {
            label: {
              en: 'Radiant Red Tintcoat Dual Tone',
              ar: 'أحمر داكن معدني ثنائي اللون'
            },
            image: 'images/2362/red_black.png',
            usdz: 'red_black',
            price: {
              KSA: 3953,
              UAE: 3879,
              OMR: 411,
              BHD: 401,
              QAR: 3837,
              JOD: 748,
              KW: 327,
              IQD: 1054,
              LBP: 1054,
              default: 3879,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Radiant Red',
                  ar: 'أحمر مشع'
                },
                price: {
                  KSA: 2156,
                  UAE: 2116,
                  OMR: 224,
                  BHD: 218,
                  QAR: 2093,
                  JOD: 408,
                  KW: 178,
                  IQD: 575,
                  LBP: 575,
                  default: 2116,
                }
              },
              {
                title: {
                  en: 'Roof Color',
                  ar: 'لون السقف'
                },
                label: {
                  en: 'Black',
                  ar: 'أسود'
                },
                price: {
                  KSA: 1797,
                  UAE: 1763,
                  OMR: 187,
                  BHD: 182,
                  QAR: 1744,
                  JOD: 340,
                  KW: 149,
                  IQD: 479,
                  LBP: 479,
                  default: 1763,
                }
              }
            ],
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2362/red_black_cart.png',
            hasQuantity: false,
            autoIntelRPOCode: 'EX07',
            brandItemCode: 'EX07',
          },
          '37xytt0490j8-my25': {
            label: {
              en: 'Radiant Red Tintcoat Dual Tone',
              ar: 'أحمر داكن معدني ثنائي اللون'
            },
            image: 'images/2362/red_black.png',
            usdz: 'red_black',
            price: {
              KSA: 3953,
              UAE: 3879,
              OMR: 411,
              BHD: 401,
              QAR: 3837,
              JOD: 748,
              KW: 327,
              IQD: 1054,
              LBP: 1054,
              default: 3879,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Radiant Red',
                  ar: 'أحمر مشع'
                },
                price: {
                  KSA: 2156,
                  UAE: 2116,
                  OMR: 224,
                  BHD: 218,
                  QAR: 2093,
                  JOD: 408,
                  KW: 178,
                  LBP: 575,
                  IQD: 575,
                  default: 2116,
                }
              },
              {
                title: {
                  en: 'Roof Color',
                  ar: 'لون السقف'
                },
                label: {
                  en: 'Black',
                  ar: 'أسود'
                },
                price: {
                  KSA: 1797,
                  UAE: 1763,
                  OMR: 187,
                  BHD: 182,
                  QAR: 1744,
                  JOD: 340,
                  KW: 149,
                  IQD: 479,
                  LBP: 479,
                  default: 1763,
                }
              }
            ],
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2362/red_black_cart.png',
            hasQuantity: false,
            autoIntelRPOCode: 'EX07',
            brandItemCode: 'EX07',
          },
          '536y2x49kxgq': {
            label: {
              en: 'Sterling Gray Metallic Dual Tone',
              ar: 'رمادي معدني ثنائي اللون'
            },
            image: 'images/2362/silver_black.png',
            usdz: 'silver_black',
            price: {
              KSA: 2691,
              UAE: 2641,
              OMR: 280,
              BHD: 273,
              QAR: 2612,
              JOD: 510,
              KW: 222,
              IQD: 718,
              LBP: 718,
              default: 2641,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Sterling Gray',
                  ar: 'ستيرلنج جراي'
                },
                price: {
                  KSA: 895,
                  UAE: 878,
                  OMR: 93,
                  BHD: 91,
                  QAR: 868,
                  JOD: 169,
                  KW: 74,
                  IQD: 239,
                  LBP: 239,
                  default: 878,
                }
              },
              {
                title: {
                  en: 'Roof Color',
                  ar: 'لون السقف'
                },
                label: {
                  en: 'Black',
                  ar: 'أسود'
                },
                price: {
                  KSA: 1797,
                  UAE: 1763,
                  OMR: 187,
                  BHD: 182,
                  QAR: 1744,
                  JOD: 340,
                  KW: 149,
                  IQD: 479,
                  LBP: 479,
                  default: 1763,
                }
              }
            ],
            index: 6,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2362/silver_black_cart.png',
            hasQuantity: false,
            autoIntelRPOCode: 'EX09',
            brandItemCode: 'EX09',
          },
          '4zd4onnmg2y5': {
            label: {
              en: 'Riptide Blue Metallic Dual Tone',
              ar: 'أزرق معدني ثنائي اللون'
            },
            image: 'images/2362/blue_black.png',
            usdz: 'blue_black',
            price: {
              KSA: 2691,
              UAE: 2641,
              OMR: 280,
              BHD: 273,
              QAR: 2612,
              JOD: 510,
              KW: 222,
              IQD: 718,
              LBP: 718,
              default: 2641,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Riptide Blue Metallic',
                  ar: 'الأزرق المعدني'
                },
                price: {
                  KSA: 895,
                  UAE: 878,
                  OMR: 93,
                  BHD: 91,
                  QAR: 868,
                  JOD: 169,
                  KW: 74,
                  IQD: 239,
                  LBP: 239,
                  default: 878,
                }
              },
              {
                title: {
                  en: 'Roof Color',
                  ar: 'لون السقف'
                },
                label: {
                  en: 'Black',
                  ar: 'أسود'
                },
                price: {
                  KSA: 1797,
                  UAE: 1763,
                  OMR: 187,
                  BHD: 182,
                  QAR: 1744,
                  JOD: 340,
                  KW: 149,
                  IQD: 479,
                  LBP: 479,
                  default: 1763,
                }
              }
            ],
            index: 7,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2362/blue_black_cart.png',
            hasQuantity: false,
            autoIntelRPOCode: 'EX08',
            brandItemCode: 'EX08',
          },
          '2phvl5sq89cx': {
            label: {
              en: 'Summit White Dual Tone',
              ar: 'قمة بيضاء ثنائية اللون'
            },
            image: 'images/2362/white_black.png',
            usdz: 'white_black',
            price: {
              KSA: 5524,
              UAE: 5421,
              OMR: 575,
              BHD: 560,
              QAR: 5362,
              JOD: 1046,
              KW: 457,
              IQD: 1473,
              LBP: 1473,
              default: 5421,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Summit White',
                  ar: 'القمة البيضاء'
                },
                price: {
                  KSA: 3728,
                  UAE: 3658,
                  OMR: 388,
                  BHD: 378,
                  QAR: 3618,
                  JOD: 706,
                  KW: 308,
                  IQD: 994,
                  LBP: 994,
                  default: 3658,
                }
              },
              {
                title: {
                  en: 'Roof Color',
                  ar: 'لون السقف'
                },
                label: {
                  en: 'Black',
                  ar: 'أسود'
                },
                price: {
                  KSA: 1797,
                  UAE: 1763,
                  OMR: 187,
                  BHD: 182,
                  QAR: 1744,
                  JOD: 340,
                  KW: 149,
                  IQD: 479,
                  LBP: 479,
                  default: 1763,
                }
              }
            ],
            index: 8,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2362/white_black_cart.png',
            hasQuantity: false,
            autoIntelRPOCode: 'EX06',
            brandItemCode: 'EX06',
          },
          '2phvl5sq89cx-my25': {
            label: {
              en: 'Summit White Dual Tone',
              ar: 'قمة بيضاء ثنائية اللون'
            },
            image: 'images/2362/white_black.png',
            usdz: 'white_black',
            price: {
              KSA: 5524,
              UAE: 5421,
              OMR: 575,
              BHD: 560,
              QAR: 5362,
              JOD: 1046,
              KW: 457,
              IQD: 1473,
              LBP: 1473,
              default: 5421,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Summit White',
                  ar: 'القمة البيضاء'
                },
                price: {
                  KSA: 3728,
                  UAE: 3658,
                  OMR: 388,
                  BHD: 378,
                  QAR: 3618,
                  JOD: 706,
                  KW: 308,
                  IQD: 994,
                  LBP: 994,
                  default: 3658,
                }
              },
              {
                title: {
                  en: 'Roof Color',
                  ar: 'لون السقف'
                },
                label: {
                  en: 'Black',
                  ar: 'أسود'
                },
                price: {
                  KSA: 1797,
                  UAE: 1763,
                  OMR: 187,
                  BHD: 182,
                  QAR: 1744,
                  JOD: 340,
                  KW: 149,
                  IQD: 479,
                  LBP: 479,
                  default: 1763,
                }
              }
            ],
            index: 8,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2362/white_black_cart.png',
            hasQuantity: false,
            autoIntelRPOCode: 'EX06',
            brandItemCode: 'EX06',
          },
          '2phvil5sq89c2': {
            label: {
              en: 'Polar White Tricoat Dual Tone',
              ar: 'لون أبيض قطبي ثلاثي الطبقات بلونين'
            },
            image: 'images/2362/Equinox_Polar_White_Dual.png',
            usdz: 'white_black',
            price: {
              KSA: 5524,
              UAE: 5421,
              OMR: 575,
              BHD: 560,
              QAR: 5362,
              JOD: 1046,
              KW: 457,
              IQD: 1473,
              LBP: 1473,
              default: 5421,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Polar White Tricoat',
                  ar: 'أبيض قطبي ثلاثي الطبقات'
                },
                price: {
                  KSA: 3728,
                  UAE: 3658,
                  OMR: 388,
                  BHD: 378,
                  QAR: 3618,
                  JOD: 706,
                  KW: 308,
                  IQD: 994,
                  LBP: 994,
                  default: 3658,
                }
              },
              {
                title: {
                  en: 'Roof Color',
                  ar: 'لون السقف'
                },
                label: {
                  en: 'Black',
                  ar: 'أسود'
                },
                price: {
                  KSA: 1797,
                  UAE: 1763,
                  OMR: 187,
                  BHD: 182,
                  QAR: 1744,
                  JOD: 340,
                  KW: 149,
                  IQD: 479,
                  LBP: 479,
                  default: 1763,
                }
              }
            ],
            index: 9,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2362/Equinox_Polar_White_Dual.png',
            hasQuantity: false,
            autoIntelRPOCode: 'EX22'
          },
          '2phvil5sq89c2-my25': {
            label: {
              en: 'Polar White Tricoat Dual Tone',
              ar: 'لون أبيض قطبي ثلاثي الطبقات بلونين'
            },
            image: 'images/2362/Equinox_Polar_White_Dual.png',
            usdz: 'white_black',
            price: {
              KSA: 5524,
              UAE: 5421,
              OMR: 575,
              BHD: 560,
              QAR: 5362,
              JOD: 1046,
              KW: 457,
              IQD: 1473,
              LBP: 1473,
              default: 5421,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Polar White Tricoat',
                  ar: 'أبيض قطبي ثلاثي الطبقات'
                },
                price: {
                  KSA: 3728,
                  UAE: 3658,
                  OMR: 388,
                  BHD: 378,
                  QAR: 3618,
                  JOD: 706,
                  KW: 308,
                  IQD: 994,
                  LBP: 994,
                  default: 3658,
                }
              },
              {
                title: {
                  en: 'Roof Color',
                  ar: 'لون السقف'
                },
                label: {
                  en: 'Black',
                  ar: 'أسود'
                },
                price: {
                  KSA: 1797,
                  UAE: 1763,
                  OMR: 187,
                  BHD: 182,
                  QAR: 1744,
                  JOD: 340,
                  KW: 149,
                  IQD: 479,
                  LBP: 479,
                  default: 1763,
                }
              }
            ],
            index: 9,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2362/Equinox_Polar_White_Dual.png',
            hasQuantity: false,
            autoIntelRPOCode: 'EX22'
          },
          '2phvil57sq89cx': {
            label: {
              en: 'Polar White Tricoat',
              ar: 'أبيض قطبي ثلاثي الطبقات'
            },
            image: 'images/2362/ext_colors/official/Polar-White-Tricoat.jpg',
            usdz: 'white_black',
            price: {
              KSA: 5524,
              UAE: 5421,
              OMR: 575,
              BHD: 560,
              QAR: 5362,
              JOD: 1046,
              KW: 457,
              IQD: 1473,
              LBP: 1473,
              default: 5421,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Polar White Tricoat',
                  ar: 'أبيض قطبي ثلاثي الطبقات'
                },
                price: {
                  KSA: 3728,
                  UAE: 3658,
                  OMR: 388,
                  BHD: 378,
                  QAR: 3618,
                  JOD: 706,
                  KW: 308,
                  LBP: 994,
                  IQD: 994,
                  default: 3658,
                }
              }
            ],
            index: 10,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2362/ext_colors/official/Polar-White-Tricoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'EX29',
            brandItemCode: 'EX29',
          },
          '2phvil57sq89cx-my25': {
            label: {
              en: 'Polar White Tricoat',
              ar: 'أبيض قطبي ثلاثي الطبقات'
            },
            image: 'images/2362/ext_colors/official/Polar-White-Tricoat.jpg',
            usdz: 'white_black',
            price: {
              KSA: 5524,
              UAE: 5421,
              OMR: 575,
              BHD: 560,
              QAR: 5362,
              JOD: 1046,
              KW: 457,
              IQD: 1473,
              LBP: 1473,
              default: 5421,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Polar White Tricoat',
                  ar: 'أبيض قطبي ثلاثي الطبقات'
                },
                price: {
                  KSA: 3728,
                  UAE: 3658,
                  OMR: 388,
                  BHD: 378,
                  QAR: 3618,
                  JOD: 706,
                  KW: 308,
                  IQD: 994,
                  LBP: 994,
                  default: 3658,
                }
              }
            ],
            index: 10,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2362/ext_colors/official/Polar-White-Tricoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'EX29',
            brandItemCode: 'EX29',
          }
        }
      },
      features: {
        '29kx4ja7eg7r': {
          label: {
            en: 'Black painted Alumin. Wheels 21 inch ',
            ar: 'عجلات ألومنیوم مطلیة بالأسود قیاس 21'
          },
          image: 'images/2362/wheel.png',
          title: {
            en: 'Black painted Alumin. Wheels 21 inch ',
            ar: 'عجلات ألومنیوم مطلیة بالأسود قیاس 21'
          },
          desc: {
            en: 'These powerful wheels are designed with aluminum surfaces in mind to remove any blemishes from your wheel and protect it from nature\'s harshest elements',
            ar: 'تم تصمیم عجلات الألومنیوم ھذه لتوفر المظھر العصري وتقاوم العوامل الجویة.'
          },
          type: 'exterior'
        },
        '3ud49gqncb2e': {
          label: {
            en: 'Welcome animated light front and rear',
            ar: 'إﻧﺎرة ﺣرﻛﯾﺔ ﺗرﺣﯾﺑﯾﺔ ﻓﻲ اﻷﻣﺎم واﻟﺧﻠف'
          },
          image: 'images/2362/front_rear_light.png',
          title: {
            en: 'Welcome animated light front and rear',
            ar: 'إﻧﺎرة ﺣرﻛﯾﺔ ﺗرﺣﯾﺑﯾﺔ ﻓﻲ اﻷﻣﺎم واﻟﺧﻠف'
          },
          desc: {
            en: 'An electric smile greets you as you approach the bold Equinox EV. The curve of the headlight is designed to make driving this electric vehicle a pleasant one.',
            ar: 'ابتسامة بقوة الكھرباء سترحب بك عند اقترابك من اكوینوكس EV الجریئة. تم تصمیم انحناءة المصابیح الأمامیة لجعل قیادة ھذه المركبة الكھربائیة في قمة المتعة.'
          },
          type: 'exterior'
        },
        '1jhh1m7485kl': {
          label: {
            en: 'Sporty grill',
            ar: 'ﺷﺑك رﯾﺎﺿﻲ'
          },
          image: 'images/2362/sporty_grill.png',
          title: {
            en: 'Sporty grill',
            ar: 'ﺷﺑك رﯾﺎﺿﻲ'
          },
          desc: {
            en: 'Look forward to a sporty grill in the Equinox EV. This well-designed grille helps to efficiently direct airflow around the car and can improve fuel efficiency too.',
            ar: 'ستحظى بمظھر ریاضي مع الشبك الأمامي في اكوینوكس EV. إنھ یساعد على توجیھ تدفق الھواء بكفاءة ویمكن أن یحسن أیضا من كفاءة استھلاك الوقود.'
          },
          type: 'exterior'
        },
        '8vlg0walqaux': {
          label: {
            en: 'Adaptive Cruise Control',
            ar: 'نظام تثبيت السرعة التكيفي'
          },
          image: 'images/2362/gm_adaptive_cruise_control.png',
          title: {
            en: 'Adaptive Cruise Control',
            ar: 'نظام تثبيت السرعة التكيفي'
          },
          desc: {
            en: 'Can automatically maintain a driver-selected gap time from the detected vehicle you’re following while you steer, reducing your need to frequently brake and accelerate while you drive.',
            ar: 'يمكن أن تحافظ تلقائيًا على وقت الفجوة الذي يختاره السائق من السيارة التي تم اكتشافها والتي تتبعها أثناء التوجيه، مما يقلل من حاجتك إلى الكبح والتسارع بشكل متكرر أثناء القيادة.'
          },
          type: 'exterior'
        },
        '8s8tx9p847si': {
          label: {
            en: 'Enhanced Auto Park Assist',
            ar: 'نظام مساعدة ركن السيارة المحسّن'
          },
          image: 'images/2362/gm_enhanced_auto_park_assist.png',
          title: {
            en: 'Enhanced Auto Park Assist',
            ar: 'نظام مساعدة ركن السيارة المحسّن'
          },
          desc: {
            en: 'Can provide distance alerts to nearby detected objects behind your vehicle to help you park and avoid collisions when you’re in Reverse at low speeds.',
            ar: 'يمكنه توفير تنبيهات المسافة للأجسام القريبة المكتشفة خلف سيارتك لمساعدتك على ركن السيارة وتجنب الاصطدامات عندما تكون في وضع الرجوع للخلف بسرعات منخفضة.'
          },
          type: 'exterior'
        },
        xjhzg2xjvike: {
          label: {
            en: 'HD Surround Vision',
            ar: 'رؤية محيطية عالية الدقة'
          },
          image: 'images/2362/gm_surround_vision.png',
          title: {
            en: 'HD Surround Vision',
            ar: 'رؤية محيطية عالية الدقة'
          },
          desc: {
            en: 'Uses multiple camera to display a digital overhead image of the area around your vehicle along with rear vision camera or front views. It works at low speeds and may help you park and avoid nearby objects.',
            ar: 'يستخدم عدة كاميرات لعرض صورة علوية رقمية للمنطقة المحيطة بسيارتك، بالإضافة إلى كاميرا الرؤية الخلفية أو الأمامية. يعمل بسرعات منخفضة، وقد يساعدك على ركن سيارتك وتجنب الأجسام القريبة.'
          },
          type: 'exterior'
        },
        g8rwatb2qokf: {
          label: {
            en: 'Intersection Auto Emergency Braking',
            ar: 'الكبح الطارئ التلقائي عند التقاطعات'
          },
          image: 'images/2362/gm_auto_emergency_braking.png',
          title: {
            en: 'Intersection Auto Emergency Braking',
            ar: 'الكبح الطارئ التلقائي عند التقاطعات'
          },
          desc: {
            en: 'Works with forward collision alert to help you avoid or reduce the severity of a front-end collision with a detected vehicle you’re following.',
            ar: 'يعمل مع تنبيه الاصطدام الأمامي لمساعدتك على تجنب أو تقليل شدة الاصطدام الأمامي بالمركبة التي تتبعها.'
          },
          type: 'exterior'
        },
        '': {
          label: {
            en: 'Lane Keep Assist With Lane Departure Warning',
            ar: 'مساعدة الحفاظ على المسار مع تحذير مغادرة المسار'
          },
          image: 'images/2362/gm_lane_assist.png',
          title: {
            en: 'Lane Keep Assist With Lane Departure Warning',
            ar: 'مساعدة الحفاظ على المسار مع تحذير مغادرة المسار'
          },
          desc: {
            en: 'Uses a brief, gentle steering wheel turn to alert you when you may be unintentionally drifting out of detected lane lines, so you can steer to stay safely in your lane.',
            ar: 'يستخدم حركة عجلة القيادة اللطيفة والقصيرة لتنبيهك عندما تنحرف عن غير قصد عن خطوط المسار المكتشفة، حتى تتمكن من التوجيه للبقاء في مسارك بأمان.'
          },
          type: 'exterior'
        },
        '80a8vikbz9mc': {
          label: {
            en: 'Convenient charging',
            ar: 'ﺷﺣن ﺳﮭل'
          },
          image: 'images/2362/convenient_charging.png',
          title: {
            en: 'Convenient charging',
            ar: 'ﺷﺣن ﺳﮭل'
          },
          desc: {
            en: 'Standard 7KW home charger§ for overnight full charge and DC fast charging capable; 55Km of range in 10mins. (*The 7kW wall box charger is included with the purchase; however, installation fees are separate and not included. Check with your local dealer for more details on installation and warranty.)',
            ar: 'شاحن منزلي قياسي بقوة 7 كيلوواط§ لشحن كامل طوال الليل مع إمكانية الشحن السريع بالتيار المستمر؛ مدى يصل إلى 55 كم في 10 دقائق. *شاحن الحائط بقوة 7 كيلوواط مُضمن مع الشراء؛ مع ذلك، رسوم التركيب مُنفصلة وغير مُضمنة. تواصل مع الوكيل المحلي لمزيد من التفاصيل حول التركيب والضمان.'
          },
          type: 'exterior'
        },
        '5axgfh2btytx': {
          label: {
            en: 'Adrenaline Red, Evotex seat material with front bucket seats',
            ar: 'مقاعد دلو أمامية'
          },
          image: 'images/2362/red_leatherseats.png',
          title: {
            en: 'Adrenaline Red, Evotex seat material with front bucket seats',
            ar: 'أحمر أدرينالين، مادة مقعد Evotex مع مقاعد دلو أمامية'
          },
          desc: {
            en: 'The Equinox EV is fitted with Ventilated Driver and Passenger Bucket seats with Adrenaline Red and Black interior giving your ride the sporty look it deserves.',
            ar: 'تم تجهيز سيارة Equinox EV بمقاعد دلوية جيدة التهوية للسائق والركاب، بالإضافة إلى تصميم داخلي باللونين الأحمر والأسود، مما يمنح سيارتك المظهر الرياضي الذي تستحقه.'
          },
          type: 'interior'
        },
        bf5spqe7qghc: {
          label: {
            en: 'Multi-color ambient interior lighting',
            ar: 'ﻣﻘﺻورة ﺑﺈﻧﺎرة ﻣﺗﻌدد اﻷﻟوان'
          },
          image: 'images/2362/ambient_light_hd.png',
          title: {
            en: 'Multi-color ambient interior lighting',
            ar: 'ﻣﻘﺻورة ﺑﺈﻧﺎرة ﻣﺗﻌدد اﻷﻟوان'
          },
          desc: {
            en: 'Light up the interior of your Equinox EV with a color that matches your style.',
            ar: 'اختر إضاءة مقصورة اكوینوكس EV بلون یتناسب مع أسلوبك وذوقك الرفیع في كل رحلة تنطلق بھا.'
          },
          type: 'interior'
        },
        '2kzy6a0jhsu2': {
          label: {
            en: 'Head-up Display',
            ar: 'ارض ازج ا'
          },
          image: 'images/2362/headup_display.png',
          title: {
            en: 'Head-up Display',
            ar: 'ارض ازج ا'
          },
          desc: {
            en: 'Keep safety in sight and your eyes on the road. The Head-up Display empowers you to stay focused and in control.',
            ar: 'لا تشغل عینیك بعیدا عن الطریق لتبقى آمنا مع میزة العرض على الزجاج الأمامي.'
          },
          type: 'interior'
        },
        '2hc2o7cuwka9': {
          label: {
            en: 'Best-in-class diagonal display 17.7" touchscreen with Google built-in.',
            ar: 'ﺟوﺟل اﻟﻣدﻣﺟﺔ ﺷﺎﺷﺔ ﻟﻣس ﻗﯾﺎس "17.7 اﻷﻓﺿل ﺿﻣن ﻓﺋﺗﮭﺎ ﻣﻊ ﻣﯾزة'
          },
          image: 'images/2362/touchscreen.png',
          title: {
            en: 'Best-in-class diagonal display 17.7" touchscreen with Google built-in.',
            ar: 'ﺟوﺟل اﻟﻣدﻣﺟﺔ ﺷﺎﺷﺔ ﻟﻣس ﻗﯾﺎس "17.7 اﻷﻓﺿل ﺿﻣن ﻓﺋﺗﮭﺎ ﻣﻊ ﻣﯾزة'
          },
          desc: {
            en: 'Google built-in powered by OnStar comes standard in the Equinox EV. So you can look forward to Google Assistant, Google Maps, Google Play and so much more - whether parked or on-the go!',
            ar: 'تأتي میزة جوجل المدمجة المدعومة من أونستار بشكل قیاسي في اكوینوكس EV. لذا یمكنك استخدام مساعد جوجل، خرائط جوجل، جوجل بلاي والمزید – سواء كنت متوقفا أو أثناء القیادة!'
          },
          type: 'interior'
        },
        '2je8obbpqtv4': {
          label: {
            en: 'Driver Information Center "11',
            ar: '"11 ﻣرﻛز ﻣﻌﻠوﻣﺎت اﻟﻘﯾﺎدة ﻗﯾﺎس'
          },
          image: 'images/2362/information_center.png',
          title: {
            en: 'Driver Information Center "11',
            ar: '"11 ﻣرﻛز ﻣﻌﻠوﻣﺎت اﻟﻘﯾﺎدة ﻗﯾﺎس'
          },
          desc: {
            en: ' Have complete vehicle control at your fingertips with the easy to see and even easier to navigate, DIC.',
            ar: 'كن متحكما بمركبتك بشكل تام وبكل سھولة مع مركز معلومات القیادة الذي یوفر لك رؤیة واضحة ومباشرة لجمیع المعلومات التي تھمك أثناء القیادة.'
          },
          type: 'interior'
        },
        '4cq85subl38y': {
          label: {
            en: 'Confident range',
            ar: 'ﻣدى طوﯾل'
          },
          image: 'images/2362/long_range.png',
          title: {
            en: 'Confident range',
            ar: 'ﻣدى طوﯾل'
          },
          desc: {
            en: 'Say hello to an estimated 513 km range -When enabled, Regen on Demand™ and One Pedal Driving helps to convert the vehicle\'s kinetic energy into energy stored in the battery, so you get the most out of every trip.',
            ar: 'ستمتع بمدى یصل إلى 513 كم! عند تفعیلھا، تساعد تقنیة إعادة الشحن عند الطلب والقیادة بدواسة واحدة على تحویل الطاقة الحركیة للمركبة إلى طاقة مخزنة في البطاریة، لتستفید من كل رحلة إلى أقصى حد.'
          },
          type: 'interior'
        },
        '5glz7rcq53uo': {
          label: {
            en: 'OnStar connected',
            ar: 'اﺗﺻﺎل أوﻧﺳﺗﺎر'
          },
          image: 'images/2362/onstar_connected.png',
          title: {
            en: 'OnStar connected',
            ar: 'اﺗﺻﺎل أوﻧﺳﺗﺎر'
          },
          desc: {
            en: 'Experience the amazing in-vehicle connected technology of OnStarⓇ in the Equinox EV, now with a 3-year OnStarⓇ Protect and Connect Plan! Connect instantly through the myChevrolet App.',
            ar: 'اختبر تقنیة الاتصال الرائعة في اكوینوكس EV المدعومة من أونستار، الآن مع خطة الحمایة والاتصال من أونستار لمدة 3 سنوات! اتصل فورا من خلال تطبیق .mychevrolet'
          },
          type: 'interior'
        },
        UeZiCrgosRRz: {
          label: {
            en: 'Chevy safety assist with more than 10 safety features',
            ar: '10 ﻣزاﯾﺎ أﻣﺎن ﻣﺳﺎﻋد اﻷﻣﺎن ﻣن ﺷﻔروﻟﯾﮫ ﻣﻊ أﻛﺛر ﻣن'
          },
          image: 'images/2362/safety_assist.png',
          title: {
            en: 'Chevy safety assist with more than 10 safety features',
            ar: '10 ﻣزاﯾﺎ أﻣﺎن ﻣﺳﺎﻋد اﻷﻣﺎن ﻣن ﺷﻔروﻟﯾﮫ ﻣﻊ أﻛﺛر ﻣن'
          },
          desc: {
            en: 'We\'ll keep you safe from sun-rides to sunset Boasting over 10 safety features (including Automatic Emergency Breaking & IntelliBeam Chevy prides itself on your safety.)',
            ar: 'انعم بالأمان وراحة البال أینما ذھبت مع أكثر من 10 مزایا للأمان (بما في ذلك الفرملة الطارئة الأوتوماتیكیة ومصابیح .IntelliBeam'
          },
          type: 'interior'
        }
      },
      accessories: {
        '3ckvx6gvve78': {
          label: {
            en: 'Solid Roof',
            ar: 'سقف صلب'
          },
          image: 'images/2362/solid_sunroof.png',
          category: {
            en: 'Roof',
            ar: 'سَطح'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            LBP: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'OP02',
          brandItemCode: 'AC215',
        },
        '3ckvx6gvve79': {
          label: {
            en: 'Sunroof',
            ar: 'فتحة السقف'
          },
          image: 'images/2362/with_sunroof.png',
          category: {
            en: 'Roof',
            ar: 'سَطح'
          },
          price: {
            KSA: 5386,
            UAE: 5285,
            OMR: 560,
            BHD: 546,
            QAR: 5228,
            JOD: 1020,
            KW: 445,
            IQD: 1436,
            LBP: 1436,
            default: 5285,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'OP01',
          brandItemCode: 'OP01',
        },
        '3ckvx6gvve67': {
          label: {
            en: 'Front Badge Illuminated',
            ar: 'الشعار الأمامي مضيء'
          },
          image: 'images/2362/front_badge_illuminated.png',
          category: {
            en: 'Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            LBP: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AC01',
          brandItemCode: 'AC01',
        },
        '60cyi4386dh7': {
          label: {
            en: 'Side Mirror Cap',
            ar: 'أغطية المرايا'
          },
          image: 'images/2362/orvm_cap.png',
          category: {
            en: 'Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            LBP: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AC02',
          brandItemCode: 'AC02',
        },
        '26awwpydj3k4': {
          label: {
            en: 'Roof CrossRails',
            ar: 'قضبان سقفية متقاطعة'
          },
          image: 'images/2362/roof_crossrails.png',
          category: {
            en: 'Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            LBP: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AC03',
          brandItemCode: 'AC207',
        },
        '41j05csmotu4': {
          label: {
            en: 'Side Mud Guards',
            ar: 'واقيات الرذاذ الجانبية'
          },
          image: 'images/2362/side_mud_gaurd.png',
          category: {
            en: 'Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            LBP: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AC04',
          brandItemCode: 'AC04',
        },
        '4g3h4ls28grj': {
          label: {
            en: 'Side Perimeter Lamps',
            ar: 'مصابيح جانبية'
          },
          image: 'images/2362/side_perimeter_lamps.png',
          category: {
            en: 'Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            LBP: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AC05',
          brandItemCode: 'AC05',
        },
        '56cy0d0cnz7u': {
          label: {
            en: 'Floor Mat',
            ar: 'سجاد الأرضية'
          },
          image: 'images/2362/floor_mat.png',
          category: {
            en: 'Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            LBP: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AC06',
          brandItemCode: 'AC06',
        },
        '2esb5gdmfpk8': {
          label: {
            en: 'Floor Liner',
            ar: 'بطانة الأرضية'
          },
          image: 'images/2362/floor_liner.png',
          category: {
            en: 'Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            LBP: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AC07',
          brandItemCode: 'AC07',
        },
        '496aqzregvz5': {
          label: {
            en: 'Trunk Cargo BoxMat',
            ar: 'سجاد صندوق الأمتعة'
          },
          image: 'images/2362/trunk_cargo_boxmat.png',
          category: {
            en: 'Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            LBP: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AC08',
          brandItemCode: 'AC08',
        },
        '4e8rsvm8yulq': {
          label: {
            en: 'Trunk Cargo Net',
            ar: 'شبك صندوق الأمتعة'
          },
          image: 'images/2362/trunk_cargo_net.png',
          category: {
            en: 'Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            LBP: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AC09',
          brandItemCode: 'AC09',
        },
        '6bmwunaxygjc': {
          label: {
            en: 'Trunk Cargo Organiser',
            ar: 'منظم صندوق الأمتعة'
          },
          image: 'images/2362/trunk_cargo_organiser.png',
          category: {
            en: 'Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            LBP: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AC10',
          brandItemCode: 'AC10',
        },
        '3l2bs2aiit7y': {
          label: {
            en: 'Trunk Cargo Shade VRS',
            ar: 'ستارة شمسية لصندوق الأمتعة'
          },
          image: 'images/2362/trunk_cargo_shade.png',
          category: {
            en: 'Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            LBP: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AC11',
          brandItemCode: 'AC230',
        },
        '50t7txtk3kvs': {
          label: {
            en: 'Trunk Cargo Tray CAV',
            ar: 'أرضية صندوق الأمتعة'
          },
          image: 'images/2362/trunk_cargo_tray.png',
          category: {
            en: 'Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            LBP: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AC12',
          brandItemCode: 'AC231',
        },
        '3ktjpvxvvvkj': {
          label: {
            en: 'Trunk Cargo Tray SHX',
            ar: 'أرضية صندوق الأمتعة'
          },
          image: 'images/2362/trunk_cargo_tray_shx.png',
          category: {
            en: 'Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            IQD: 0,
            LBP: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AC12',
          brandItemCode: 'AC232',
        }
      },
      packages: {},
      interactions: {
        f9s7b4hdeiz7: {
          label: {
            en: 'Doors',
            ar: 'الأبواب'
          },
          type: 'interaction'
        },
        '1nad76ictp6j': {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة'
          },
          type: 'interaction'
        },
        '2mbv5mgd2j0t': {
          label: {
            en: 'Sunroof',
            ar: 'فتحة السقف'
          },
          type: 'interaction'
        },
        '3cqy8q29b96c': {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف'
          },
          type: 'interaction'
        },
        '2ca59s6yd3u9': {
          label: {
            en: 'DRL',
            ar: 'DRL'
          },
          type: 'interaction'
        },
        '1dzdmvhl8vp3': {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي'
          },
          type: 'interaction'
        }
      },
      hotspots: {
        exteriorCamera: {
          title: {
            en: 'Exterior',
            ar: 'الخارج'
          }
        },
        interiorCamera: {
          title: {
            en: 'First Row Seat',
            ar: 'مقعد الصف الأول'
          }
        },
        interiorCamera_1: {
          title: {
            en: 'Second Row Seat',
            ar: 'مقعد الصف الثاني'
          }
        }
      },
      name: {
        en: 'Equinox EV',
        ar: 'Equinox EV'
      },
      specifications: {
        heading: {
          en: 'Equinox EV',
          ar: 'Equinox EV'
        },
        trim: {
          en: '',
          ar: ''
        },
        price: {
          en: '',
          ar: ''
        },
        model: 'Equinox EV',
        seatingCapacity: '5 Seats',
        dimensions: {
          length: '',
          width: '',
          height: '',
          wheelbase: '2730 mm',
          groundClearance: ''
        },
        performance: [
          {
            value: '213',
            unit: '',
            label: {
              en: '',
              ar: ''
            }
          },
          {
            value: '8',
            unit: '',
            label: {
              en: '0-100 km*',
              ar: '0إلى100 كم*'
            }
          },
          {
            value: '513',
            unit: '',
            label: {
              en: 'Range**',
              ar: 'مدى**'
            }
          }
        ],
        summary: [
          {
            title: {
              en: 'Model',
              ar: 'نموذج'
            },
            subtitle: {
              en: 'Equinox EV',
              ar: 'Equinox EV'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Exterior Colors',
              ar: 'الألوان الخارجية'
            },
            subtitle: {
              en: 'Summit White,Black, Radiant Red Tintcoat, Riptide Blue Metallic, Sterling Gray Metallic',
              ar: 'أبيض قمة، أسود، أحمر مشع، أزرق ريب تايد معدني، رمادي ستيرلينج معدني'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Interior',
              ar: 'الداخلية'
            },
            subtitle: {
              en: 'Adrenaline Red',
              ar: 'الأدرينالين الأحمر'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Wheelbase',
              ar: 'قاعدة العجلات'
            },
            subtitle: {
              en: '2730 mm',
              ar: '2730 mm'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Cargo Space',
              ar: 'سعة الأمتعة'
            },
            subtitle: {
              en: '1798 Litres (With rear seats folded flat. Cargo and load capacity limited by weight and distribution)',
              ar: '١٧٩٨ لترًا (مع طي المقاعد الخلفية بشكل مسطح. سعة الحمولة والحمولة محدودة بالوزن والتوزيع)'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Powertrain',
              ar: 'مجموعة نقل الحركة'
            },
            subtitle: {
              en: 'Front Wheel Drive',
              ar: 'الدفع بالعجلات الأمامية'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'OnStar Basics',
              ar: 'باقة OnStar الأساسية'
            },
            subtitle: {
              en: '8 Years',
              ar: '8 سنوات'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'OnStar One',
              ar: 'باقة OnStar One'
            },
            subtitle: {
              en: 'Upto 3 Years',
              ar: 'حتى 3 سنوات'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Battery Warranty',
              ar: 'ضمان البطارية'
            },
            subtitle: {
              en: '8 Years',
              ar: '8 سنوات'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Seating Capacity',
              ar: 'سعة الجلوس'
            },
            subtitle: {
              en: '5 Seats',
              ar: '5 مقاعد'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Infotainment Screen',
              ar: 'شاشة المعلومات والترفيه'
            },
            subtitle: {
              en: '17.7 diagonal advanced color LCD display with Google built-in compatibility',
              ar: 'شاشة LCD ملونة متطورة مقاس 17.7 بوصة مع توافق مدمج مع Google'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          }
        ]
      }
    }
  },
  plugins: {
    deferredAssetLoading: true,
  },
  staticTranslations: {
    specDetails: {
      model: {
        en: 'Model',
        ar: 'نموذج',
      },
      exteriorColours: {
        en: 'Exterior',
        ar: 'الخارجة',
      },
      interiorAmbience: {
        en: 'Interior',
        ar: 'الداخلية',
      },
      seatingCapacity: {
        en: 'Seating Capacity',
        ar: 'سعة المقاعد',
      },
      length: {
        en: 'Length',
        ar: 'الطول',
      },
      width: {
        en: 'Width',
        ar: 'العرض',
      },
      height: {
        en: 'Height',
        ar: 'الارتفاع',
      },
      wheelbase: {
        en: 'Wheelbase',
        ar: 'قاعدة العجلات',
      },
      groundClearance: {
        en: 'Ground Clearance',
        ar: 'الخلوص الأرضي',
      },
      share: {
        en: 'Share',
        ar: 'يشارك',
      },
    },
    variantSelection: {
      title: {
        en: 'Model',
        ar: 'نموذج',
      },
      select: {
        en: 'Select',
        ar: 'تحديد',
      },
      cancel: {
        en: 'Cancel',
        ar: 'إلغاء',
      },
      selectBodyStyle: { en: 'Select Body Style', ar: 'اختر نمط الهيكل' },
      availableTrims: { en: 'Select Trim', ar: 'اختر الفئة' },
      driveType: { en: 'Drive Type', ar: 'نوع القيادة' },
      model: { en: 'Model', ar: 'الموديل' },
    },
    loadingScreen: {
      default: {
        en: 'Preparing the canvas for your dream car. Just a few more seconds to go!',
        ar: 'لحظات ونبدأ رحلة تصميم سيارة أحلامك... باقي ثوانٍ قليلة!',
      },
      enterConfigurator: {
        en: 'Build Your Ride',
        ar: 'ابنِ سيارتك',
      },
      loading: { en: 'Loading', ar: 'جارٍ التحميل' },
      variantLoading: { en: 'Trim is loading', ar: 'نموذج جارٍ التحميل' },
      viewLoading: {
        en: 'Applying final touches',
        ar: 'تطبيق اللمسات النهائية',
      },
      disclaimer: {
        en: 'Disclaimer',
        ar: 'تنصل',
      },
      termsConditions: {
        en: 'Terms & Conditions',
        ar: 'الشروط والأحكام',
      },
      viewSpecDetails: {
        en: 'View Spec Details',
        ar: 'ععرض تفاصيل المواصفات',
      },
      disclaimerContent: {
        en: 'Prices are subject to change without prior notice and may vary based on location, availability, or other factors.',
        ar: 'الأسعار عرضة للتغيير دون إشعار مسبق وقد تختلف بناءً على الموقع والتوافر وعوامل أخرى.',
      },
      termsModal: {
        title: {
          en: 'Configurator Use Terms and Conditions',
          ar: 'شروط وأحكام استخدام المُكوِّن',
        },
        lead: {
          en: 'By using this vehicle configurator and submitting your build, you acknowledge and agree to the following:',
          ar: 'من خلال استخدام مُكوِّن تكوين السيارة هذا وإرسال تصميمك، فإنك تقر وتوافق على ما يلي:',
        },
        sections: {
          item1: {
            title: {
              en: 'Non-Binding Submission',
              ar: 'تقديم غير ملزم',
            },
            content: {
              en: 'Submitting your vehicle configuration does not constitute a formal vehicle order or purchase agreement. No payment or deposit is collected at this stage.',
              ar: 'إرسال مواصفات سيارتك لا يُعد طلبًا رسميًا للسيارة أو اتفاقية شراء. لا يتم تحصيل أي مبلغ أو دفعة مقدمة في هذه المرحلة.',
            },
          },
          item2: {
            title: {
              en: 'Preliminary Estimate',
              ar: 'تقدير أولي',
            },
            content: {
              en: 'All pricing displayed is Manufacturer’s Suggested Retail Price (MSRP)and is provided for informational purposes only. Final pricing, taxes, fees, and applicable offers will be confirmed by the authorized dealer.',
              ar: 'جميع الأسعار المعروضة هي سعر التجزئة المقترح من الشركة المصنعة، وهي لأغراض إعلامية فقط. سيتم تأكيد الأسعار النهائية والضرائب والرسوم والعروض المطبقة من قبل الوكيل المعتمد.',
            },
          },
          item3: {
            title: {
              en: 'Dealer Follow-Up',
              ar: 'متابعة التاجر',
            },
            content: {
              en: 'Your submitted configuration will be shared with a participating dealer, who will contact you to discuss your preferences, availability, and next steps.',
              ar: 'سيتم مشاركة التكوين الذي أرسلته مع أحد التجار المشاركين، والذي سيتصل بك لمناقشة تفضيلاتك وتوافرك والخطوات التالية.',
            },
          },
          item4: {
            title: {
              en: 'Availability and Fulfillment',
              ar: 'التوفر والوفاء',
            },
            content: {
              en: 'We will make every reasonable effort to fulfill your preferences; however, final vehicle and accessories selection is subject to dealer inventory and production availability. Configurations may be limited to in-stock or incoming vehicles.',
              ar: 'سنبذل قصارى جهدنا لتلبية تفضيلاتكم، إلا أن الاختيار النهائي للمركبات والملحقات يخضع لمخزون الوكيل وتوافر الإنتاج. قد تقتصر التكوينات على المركبات المتوفرة في المخزون أو الواردة.',
            },
          },
          item5: {
            title: {
              en: 'No Obligation',
              ar: 'لا يوجد التزام',
            },
            content: {
              en: 'You are under no obligation to purchase the configured vehicle, and submitting your configuration does not create a contractual relationship between you and the manufacturer or dealer.',
              ar: 'لا يوجد أي التزام عليك بشراء السيارة المخصصة، كما أن إرسال التكوين الخاص بك لا يؤدي إلى إنشاء علاقة تعاقدية بينك وبين الشركة المصنعة أو التاجر.',
            },
          },
          item6: {
            title: {
              en: 'Use of Personal Information and Consent',
              ar: 'استخدام المعلومات الشخصية والموافقة',
            },
            content: {
              en: 'By submitting your configuration, you consent to the collection, use, and sharing of your personal information with authorized dealers to facilitate your request, in accordance with our [Privacy Statement].',
              ar: 'من خلال إرسال التكوين الخاص بك، فإنك توافق على جمع معلوماتك الشخصية واستخدامها ومشاركتها مع التجار المعتمدين لتسهيل طلبك، وفقًا لـ [بيان الخصوصية] الخاص بنا.',
            },
          },
          item7: {
            title: {
              en: 'System Limitations',
              ar: 'قيود النظام',
            },
            content: {
              en: 'While we aim to provide accurate and current information, the configurator may not reflect all available features or up-to-date pricing. Errors or omissions may occur.',
              ar: 'مع أننا نهدف إلى توفير معلومات دقيقة وحديثة، إلا أن مُكوِّن الإعدادات قد لا يعكس جميع الميزات المتاحة أو الأسعار المُحدَّثة. قد تحدث أخطاء أو سهو.',
            },
          },
        },
      },
    },
    common: {
      exit: {
        en: 'Exit',
        ar: 'خروج',
      },
      close: {
        en: 'Close',
        ar: 'إغلاق',
      },
      cancel: {
        en: 'Cancel',
        ar: 'إلغاء',
      },
      back: {
        en: 'Back',
        ar: 'خلف',
      },
      next: {
        en: 'Next',
        ar: 'التالي',
      },
      done: {
        en: 'Done',
        ar: 'تم',
      },
      exterior: {
        en: 'Exterior',
        ar: 'الخارجة',
      },
      interior: {
        en: 'Interior',
        ar: 'الداخلية',
      },
      enterFullscreen: {
        en: 'Enter Fullscreen',
        ar: 'ملء الشاشة',
      },
      exitFullscreen: {
        en: 'Exit Fullscreen',
        ar: 'إلغاء ملء الشاشة',
      },
      selectTrim: {
        en: 'Select Trim',
        ar: 'اختر الفئة',
      },
      viewConfiguration: {
        en: 'View Configuration',
        ar: 'عرض التكوين',
      },
      togglePanelTooltip: {
        en: 'Select Trim and Configuration',
        ar: 'اختر الفئة والتكوين',
      },
      nextStepTooltip: {
        en: 'Click to proceed to the next step',
        ar: 'انقر للانتقال إلى الخطوة التالية',
      },
      heading: {
        en: 'Standard Features',
        ar: 'المميزات  القياسية',
      },
      description: {
        en: 'Chevrolet Arabia',
        ar: 'شيفروليه العربية',
      },
      goBack: { en: 'Go Back', ar: 'العودة' },
      freeRoamView: { en: 'Free Roam View', ar: 'عرض التجوال الحر' },
      featureView: { en: 'Featured View', ar: 'عرض مميز' },
      configurator: { en: 'Configurator', ar: 'المُكوِّن' },
      buildYourRide: { en: 'Build Your Ride', ar: 'ابنِ سيارتك' },
      options: { en: 'Options', ar: 'الخيارات' },
      summary: { en: 'Summary', ar: 'الملخص' },
      saveYourBuild: { en: 'Save Your Build', ar: 'احفظ تكوينك' },
      startingPrice: { en: 'Starting Price', ar: 'السعر الابتدائي' },
      msrp: { en: 'MSRP', ar: 'سعر التجزئة المقترح' },
      proceed: { en: 'Proceed', ar: 'متابعة' },
    },
    tabs: {
      config: { en: 'Config', ar: 'الإعداد' },
      model: { en: 'Model', ar: 'الموديل' },
      exterior: { en: 'Exterior', ar: 'الخارجي' },
      interior: { en: 'Interior', ar: 'الداخلي' },
      options: { en: 'Options', ar: 'الخيارات' },
      summary: { en: 'Summary', ar: 'الملخص' },
    },
    cart: {
      title: {
        en: 'Your Order',
        ar: 'طلبك',
      },
      disclaimer: {
        en: 'Accessory prices and availability will be confirmed at the showroom.',
        ar: 'سيتم تأكيد أسعار الملحقات وتوافرها في صالة العرض.',
      },
      exteriorLabel: {
        en: 'Exterior:',
        ar: ':خارجي',
      },
      interiorLabel: {
        en: 'Interior:',
        ar: ':داخلي',
      },
      addedAccessories: {
        en: 'Added Accessories',
        ar: 'الإكسسوارات المضافة',
      },
      priceEstimate: {
        en: 'Price Estimate',
        ar: 'السعر التقديري',
      },
      priceBreakdown: {
        en: 'Price Breakdown',
        ar: 'تفاصيل السعر',
      },
      reserveNow: {
        en: 'Proceed',
        ar: 'متابعة',
      },
      removeAccessory: {
        en: 'Remove accessory',
        ar: 'إزالة الإكسسوار',
      },
      removeItem: { en: 'Remove', ar: 'إزالة' },
      packageCategory: { en: 'Package', ar: 'حزمة' },
    },
    saveDialog: {
      optionsTitle: {
        en: 'How would you like to proceed?',
        ar: 'كيف تريد المتابعة؟',
      },
      optionWhatsapp: { en: 'Save to WhatsApp', ar: 'حفظ على واتساب' },
      optionEmail: { en: 'Save to Email', ar: 'حفظ على البريد الإلكتروني' },
      optionTestDrive: {
        en: 'Book a Test Drive',
        ar: 'احجز اختبار قيادة',
      },
      optionCallback: {
        en: 'Request a call Back',
        ar: 'طلب معاودة الاتصال',
      },
      labelFirstName: {
        en: 'First Name*',
        ar: 'الاسم الأول',
      },
      labelLastName: {
        en: 'Last Name*',
        ar: 'اسم العائلة',
      },
      labelEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      labelMobileNumber: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      labelPrefix: {
        en: 'Prefix',
        ar: 'البادئة',
      },
      labelModel: {
        en: 'Model',
        ar: 'نموذج',
      },
      labelDealership: {
        en: 'Preferred Dealership*',
        ar: 'الوكيل المفضّل',
      },
      labelComments: {
        en: 'Comments',
        ar: 'تعليقات',
      },
      labelTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      labelTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      placeholderTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderFirstName: {
        en: 'First Name*',
        ar: 'الاسم الأول',
      },
      placeholderLastName: {
        en: 'Last Name*',
        ar: 'اسم العائلة',
      },
      placeholderEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      placeholderMobileNumber: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      placeholderComments: {
        en: 'Comments',
        ar: 'تعليقات',
      },
      placeholderSelectDealership: {
        en: 'Preferred Dealership*',
        ar: 'الوكيل المفضّل',
      },
      privacyTitle: {
        en: 'Privacy statement & legal disclaimer',
        ar: 'بيان الخصوصية وإخلاء المسؤولية القانونية',
      },
      privacyText: {
        en: 'I acknowledge and understand that my information will be shared with General Motors, its affiliates, dealer network or other parties are required by law for compliance, safety campaigns, government inquiries, or similar legal process. My information may also be shared for product research and development purposes, and to manage customer relationships to provide support. GM’s Privacy Statement addresses how GM handles the personal information shared with us.',
        ar: 'أقر وأتفهم بأنة سيتم مشاركة معلوماتي مع شركة جنرال موتورز، والشركات التابعة لها، وشبكة الوكلاء أو الأطراف الأخرى بموجب القانون للامتثال وحملات السلامة واستفسارات الحكومية وأي إجراء قانوني مماثل. وقد تتم مشاركة معلوماتي أيضًا لأغراض البحث وتطوير المنتجات ، وإدارة علاقة العملاء لتوفير الدعم اللازم. يوضح بيان خصوصية جنرال موتورز على موقعنا كيفية تعامل جنرال موتورز مع المعلومات الشخصية التي تمت مشاركتها معنا.',
      },
      privacyReadMore: {
        en: 'Read more',
        ar: 'اقرأ المزيد',
      },
      privacyMarketingConsent: {
        en: 'I wish to receive any marketing information or have my information shared with third parties for purposes of providing me with marketing information.',
        ar: 'أرغب في تلقي معلومات تسويقية أو مشاركة معلوماتي مع جهات خارجية لغاية تزويدي بمعلومات تسويقية',
      },
      buttonSend: {
        en: 'Send Configuration',
        ar: 'إرسال التكوين',
      },
      buttonSending: {
        en: 'Submitting...',
        ar: 'تقديم...',
      },
      buttonSent: {
        en: 'Sent!',
        ar: 'مرسل!',
      },
      buttonRetry: {
        en: 'Retry',
        ar: 'إعادة المحاولة',
      },
      buttonClose: {
        en: 'Okay, Got it',
        ar: 'حسنًا، فهمت',
      },
      successTitle: {
        en: 'Saved',
        ar: 'محفوظ',
      },
      successLabelModel: {
        en: 'Model Name',
        ar: 'اسم النموذج',
      },
      successLabelVariant: {
        en: 'Variant Name',
        ar: 'اسم البديل',
      },
      successLabelName: {
        en: 'Name',
        ar: 'اسم',
      },
      successLabelEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      successLabelMobile: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      qrTitle: {
        en: 'Your Unique Configuration QR',
        ar: 'رمز الاستجابة السريعة لتكوينك',
      },
      qrDescription: {
        en: 'Show this QR code to your nearest dealer to access your saved configuration.',
        ar: 'أظهر رمز الاستجابة السريعة هذا لتاجر التجزئة الأقرب للوصول إلى التكوين المحفوظ.',
      },
      qrNoteLabel: {
        en: 'Note:',
        ar: 'ملاحظة:',
      },
      qrNote: {
        en: 'Only authorized dealers can view this configuration.',
        ar: 'يمكن للوكلاء المعتمدين فقط عرض هذا التكوين.',
      },
      qrDownload: {
        en: 'Download QR',
        ar: 'تحميل QR',
      },
      qrSave: {
        en: 'Save QR',
        ar: 'حفظ QR',
      },
      errorTitle: {
        en: 'Submission Failed',
        ar: 'فشل التقديم',
      },
      errorMessage: {
        en: 'We encountered an error, please try again.',
        ar: 'واجهنا خطأ، يرجى المحاولة مرة أخرى.',
      },
    },
    featureGroups: {
      exteriorDesign: {
        en: 'Exterior Design & Styling',
        ar: 'التصميم الخارجي والتصميم',
      },
      interiorComfort: {
        en: 'Interior Comfort & Features',
        ar: 'الراحة والميزات الداخلية',
      },
      technologyConvenience: {
        en: 'Technology & Convenience',
        ar: 'التكنولوجيا والراحة',
      },
      safetyAssistance: {
        en: 'Safety & Driver Assistance',
        ar: 'السلامة ومساعدة السائق',
      },
    },
    sidePanel: {
      menu: { en: 'Main Menu', ar: 'القائمة الرئيسية' },
      search: { en: 'Search', ar: 'بحث' },
      settings: { en: 'Settings', ar: 'الإعدادات' },
      profile: { en: 'Profile', ar: 'الملف الشخصي' },
      notifications: { en: 'Notifications', ar: 'الإشعارات' },
      help: { en: 'Help & Support', ar: 'المساعدة والدعم' },
      filter: { en: 'Filter Options', ar: 'خيارات التصفية' },
      favorites: { en: 'Favorites', ar: 'المفضلة' },
      history: { en: 'History', ar: 'التاريخ' },
      tools: { en: 'Tools', ar: 'الأدوات' },
      variants: { en: 'Variants', ar: 'الأنواع' },
      exteriorColor: { en: 'Exterior Color', ar: 'لون الهيكل' },
      interiorColor: { en: 'Interior Color', ar: 'ألوان المقصورة' },
      features: { en: 'Features', ar: 'الميزات' },
      step: { en: 'Step', ar: 'خطوة' },
      openPanel: { en: 'Open Panel', ar: 'فتح اللوحة' },
      closePanel: { en: 'Close Panel', ar: 'إغلاق اللوحة' },
    },
    landscape: {
      message: {
        en: 'Please rotate your device to portrait mode',
        ar: 'يرجى تدوير جهازك إلى الوضع الرأسي',
      },
      subtitle: {
        en: 'For the best experience, please turn your device upright.',
        ar: 'للحصول على أفضل تجربة، يرجى إمساك جهازك بشكل رأسي.',
      },
    },
    cameraViewToggle: {
      ext: { en: 'Ext', ar: 'خارجي' },
      int: { en: 'Int', ar: 'داخلي' },
      dualTone: { en: 'Dual-tone', ar: 'لون مزدوج' },
    },
    bottomDrawer: {
      variants: { en: 'Variants', ar: 'الأنواع' },
      interactions: { en: 'Interactions', ar: 'التفاعلات' },
      accessories: { en: 'Options & Accessories', ar: 'الإكسسوارات' },
      features: { en: 'Features', ar: 'الميزات' },
      highlights: { en: 'Highlights', ar: 'الميزات البارزة' },
      moreOptions: { en: 'More Options', ar: 'المزيد من الخيارات' },
      colorSelector: { en: 'Color Selector', ar: 'محدد اللون' },
      exteriorColor: { en: 'Exterior Color', ar: 'لون الهيكل' },
      interiorColor: { en: 'Interior Color', ar: 'لون المقصورة' },
      wheels: { en: 'Wheels', ar: 'العجلات' },
      roofs: { en: 'Roofs', ar: 'الأسقف' },
      performance: { en: 'Performance', ar: 'الأداء' },
      packages: { en: 'Packages', ar: 'الحزم' },
      chargingOptions: { en: 'Charging Options', ar: 'خيارات الشحن' },
      safetyServices: { en: 'Safety & Services', ar: 'السلامة والخدمات' },
      expand: { en: 'Expand', ar: 'توسيع' },
      minimize: { en: 'Minimize', ar: 'تصغير' },
      freeRoamView: { en: 'Free Roam View', ar: 'عرض التجوال الحر' },
      featureView: { en: 'Featured View', ar: 'العرض المميز' },
    },
    qr: {
      scan: { en: 'Scan QR', ar: 'امسح رمز الاستجابة السريعة' },
      scanWithPhone: {
        en: 'Scan with your phone to view in your Space',
        ar: 'امسح باستخدام هاتفك لعرضه في مساحتك',
      },
    },
    share: {
      title: { en: 'Share Configuration', ar: 'مشاركة التكوين' },
      description: {
        en: 'Share your vehicle configuration with others',
        ar: 'شارك تكوين سيارتك مع الآخرين',
      },
      twitterTitle: {
        en: 'Check out my Equinox EV configuration!',
        ar: 'شاهد تكوين سيارتي Equinox EV!',
      },
      whatsappTitle: {
        en: 'Check out my Equinox EV configuration!',
        ar: 'شاهد تكوين سيارتي Equinox EV!',
      },
      linkedinTitle: {
        en: 'My Equinox EV Configuration',
        ar: 'تكويني لسيارة Equinox EV',
      },
      linkedinSummary: {
        en: 'Check out my custom Equinox EV configuration!',
        ar: 'شاهد تكويني المخصص لسيارة Equinox EV!',
      },
      emailSubject: {
        en: 'Check out my Equinox EV Configuration',
        ar: 'شاهد تكوين سيارتي Equinox EV',
      },
      emailBody: {
        en: 'I configured my dream Equinox EV. Take a look:',
        ar: 'قمت بتكوين سيارة Equinox EV المثالية. ألق نظرة:',
      },
      linkLabel: { en: 'Or copy link:', ar: 'أو انسخ الرابط:' },
      copied: {
        en: 'Copied',
        ar: 'تم نسخ الرابط إلى الحافظة!',
      },
      copy: { en: 'Copy', ar: 'نسخ' },
      saveAndShare: { en: 'Save & Share', ar: 'حفظ ومشاركة' },
      copyLink: { en: 'Copy link', ar: 'نسخ الرابط' },
      facebook: { en: 'Facebook', ar: 'Facebook' },
      twitter: { en: 'Twitter', ar: 'Twitter' },
      gmail: { en: 'Gmail', ar: 'Gmail' },
      whatsapp: { en: 'Whatsapp', ar: 'Whatsapp' },
    },
    topBar: {
      matchingVehiclesFound: {
        en: 'Matching Vehicles Found',
        ar: 'تم العثور على المركبات المطابقة',
      },
    },
    bottomBar: {
      priceLabel: { en: 'ex showroom price', ar: 'سعر صالة العرض' },
      leads: { en: 'Save', ar: 'حفظ' },
      findLocalMatches: {
        en: 'Find Local Matches',
        ar: 'اعثر على التطابقات المحلية',
      },
      save: { en: 'Save', ar: 'حفظ' },
      item: { en: 'Item', ar: 'العنصر' },
      items: { en: 'Items', ar: 'عناصر' },
      totalToPay: { en: 'Total to pay', ar: 'الإجمالي للدفع' },
      bookTestDrive: { en: 'Save', ar: 'حفظ' },
      requestQuote: { en: 'Request a Quote', ar: 'طلب عرض سعر' },
      downloadBrochure: { en: 'Download Brochure', ar: 'تحميل الكتيب' },
    },
    priceSummary: {
      title: { en: 'Price Summary', ar: 'ملخص السعر' },
      enterLocation: { en: 'Enter Location', ar: 'أدخل الموقع' },
      selectCountry: { en: 'Location', ar: 'الموقع' },
      selectCity: { en: 'Location', ar: 'الموقع' },
      note: {
        en: 'Note : Prices might vary by state and all prices listed are on road price, T&C apply.',
        ar: 'ملاحظة: قد تختلف الأسعار حسب الولاية وجميع الأسعار المذكورة هي الأسعار على الطريق، تطبق الشروط والأحكام.',
      },
      breakdown: { en: 'Price Breakdown', ar: 'تفصيل السعر' },
      color: { en: 'Color', ar: 'اللون' },
      variant: { en: 'Variant', ar: 'الفئة' },
      included: { en: 'Included', ar: 'مشمول' },
      total: { en: 'Total', ar: 'الإجمالي' },
    },
    featureDialog: {
      playPause: { en: 'Play-Pause', ar: 'تشغيل-إيقاف' },
    },
    selectDealership: {
      title: { en: 'Select a Dealer', ar: 'اختر وكيلاً' },
      subtitle: {
        en: 'Please select a dealer to find local matches similar to your configuration',
        ar: 'يرجى اختيار وكيل للعثور على سيارات مطابقة لتكوينك في منطقتك',
      },
      city: { en: 'UAE', ar: 'الإمارات' },
      dealersIn: { en: 'Dealers in', ar: 'وكلاء في' },
      dealersInCity: { en: 'Dealers in {{city}}', ar: 'وكلاء في {{city}}' },
      availableInLocations: {
        en: 'Available in {{count}} locations of {{city}}',
        ar: 'متوفرة في {{count}} مواقع في {{city}}',
      },
      proceed: {
        en: 'Select Dealer and Proceed to Configurator',
        ar: 'اختر الوكيل وتابع إلى المتكون',
      },
    },
    countryNames: {
      UAE: { en: 'UAE', ar: 'الإمارات' },
      KSA: { en: 'KSA', ar: 'السعودية' },
      KW: { en: 'Kuwait', ar: 'الكويت' },
      default: { en: 'UAE', ar: 'الإمارات' },
    },
    localMatches: {
      exterior: { en: 'Exterior', ar: 'خارجي' },
      interior: { en: 'Interior', ar: 'داخلي' },
      dualTone: { en: 'Dual-tone', ar: 'لون مزدوج' },
      yourConfiguration: { en: 'Current Configuration', ar: 'التكوين الحالي' },
      viewConfiguration: {
        en: 'View your configuration details',
        ar: 'عرض تفاصيل تكوينك',
      },
      yourBuildDetails: { en: 'Your Build Details', ar: 'تفاصيل بنائك' },
      options: { en: 'Options', ar: 'الخيارات' },
      save: { en: 'Save', ar: 'حفظ' },
      dealersIn: { en: 'Dealers in', ar: 'وكلاء في' },
      changeDealer: { en: 'Change Dealer', ar: 'تغيير الوكيل' },
      carsAvailable: {
        en: 'Cars Available at the Selected Dealership',
        ar: 'السيارات المتوفرة لدى الوكيل المختار',
      },
      matchesFound: { en: 'Matches found', ar: 'تطابق تم العثور عليه' },
      startingFrom: { en: 'Starting from', ar: 'يبدأ من' },
      getQuote: { en: 'Proceed', ar: 'احصل على عرض سعر' },
      viewDifferences: { en: 'View Differences', ar: 'عرض الاختلافات' },
      match: { en: 'Match', ar: 'تطابق' },
      closestMatchFound: {
        en: 'Closest Match Found',
        ar: 'أقرب تطابق تم العثور عليه',
      },
      buildDetails: { en: 'Build Details', ar: 'تفاصيل التكوين' },
      trim: { en: 'Trim', ar: 'الفئة' },
      matchScoreTooltip: {
        en: 'This score indicates how closely this vehicle matches your configuration.',
        ar: 'يشير هذا النتيجة إلى مدى تطابق هذه السيارة مع تكوينك.',
      },
      exactMatch: { en: 'Exact Match', ar: 'تطابق تام' },
      similarMatch: { en: 'Close Match', ar: 'تطابق قريب' },
      availableNow: { en: 'Available Now', ar: 'متوفر الآن' },
      whatYouConfigured: { en: 'What you configured', ar: 'ما قمت بتكوينه' },
      matchingInStock: { en: 'Matching vehicle available in stock', ar: 'مركبة مطابقة متوفرة في المخزون' },
      noMatches: {
        en: 'No matching vehicles found in your area.',
        ar: 'لم يتم العثور على سيارات مطابقة في منطقتك.',
      },
      closeMatchDetails: { en: 'Close Match Details', ar: 'تفاصيل التطابق القريب' },
      matchesYourSelection: { en: 'Matches your selection', ar: 'يطابق اختيارك' },
      sameAsWhatYouSelected: { en: 'Same as what you selected', ar: 'نفس ما اخترته' },
      replacesYourSelection: { en: 'Replaces your selection', ar: 'يستبدل اختيارك' },
      selectionNotIncluded: { en: 'Selection not included', ar: 'الاختيار غير مشمول' },
      includedInThisVehicle: { en: 'Included in this vehicle', ar: 'مشمول في هذه المركبة' },
      additionalFeatureAlreadyIncluded: { en: 'Additional feature already included', ar: 'ميزة إضافية مشمولة بالفعل' },
      stockDisclaimer: { en: 'Available stock may include extra features not selected in your original configuration.', ar: 'قد تشمل المركبات المتاحة ميزات إضافية لم تختارها في تكوينك الأصلي.' },
      additionalInstalledOptionsLegend: { en: 'Included in this vehicle', ar: 'مشمول في هذه المركبة' },
      vehicleDetails: { en: 'Vehicle Details', ar: 'تفاصيل المركبة' },
      installedOptionsTitle: { en: 'Comes with vehicle', ar: 'يأتي مع المركبة' },
      additionalInstalledOptionsSubtitle: { en: 'Additional Installed Options:', ar: 'الخيارات المركبة الإضافية:' },
      specTrim: { en: 'Trim', ar: 'الفئة' },
      specExteriorColor: { en: 'Exterior Color', ar: 'اللون الخارجي' },
      specInteriorColor: { en: 'Interior Color', ar: 'اللون الداخلي' },
      specWheels: { en: 'Wheels', ar: 'العجلات' },
      specPackage: { en: 'Package', ar: 'الحزمة' },
      title: {
        en: 'Configurations Found',
        ar: 'تكوينات تم العثور عليها',
      },
      message: {
        en: 'We Found a few local matches similar to your current configuration. Click to view Matches',
        ar: 'وجدنا بعض التطابقات المحلية المشابهة لتكوينك الحالي. انقر لعرض التطابقات',
      },
    },
    trim: {
      selection: { en: 'Trim Selection', ar: 'اختيار الفئة' },
    },
    paymentPage: {
      exteriorColor: { en: 'Exterior Color', ar: 'لون الهيكل' },
      interiorColor: { en: 'Interior Color', ar: 'لون المقصورة' },
      accessory: { en: 'Accessory', ar: 'ملحق' },
      closePayment: { en: 'Close', ar: 'إغلاق' },
    },
    packageModal: {
      includes: { en: 'Includes:', ar: 'يشمل:' },
      of: { en: 'of', ar: 'من' },
      totalPriceChange: {
        en: 'Total Price Change:',
        ar: 'إجمالي تغيير السعر:',
      },
      item: { en: 'Item', ar: 'غرض' },
      items: { en: 'Items', ar: 'أغراض' },
      addPackage: { en: 'Add Package', ar: 'إضافة الحزمة' },
      noThanks: { en: 'No Thanks', ar: 'لا شكراً' },
      alreadyApplied: { en: 'Already Applied', ar: 'مُطبّق بالفعل' },
    },
    changesRequired: { en: 'Changes Required', ar: 'التغييرات المطلوبة' },
    changesRequiredDescription: {
      en: 'The package you selected conflicts with your current selections. Please review the changes below.',
      ar: 'الحزمة التي اخترتها تتعارض مع اختياراتك الحالية. يرجى مراجعة التغييرات أدناه.',
    },
    itemsThatWillBeRemoved: {
      en: 'Items that will be removed',
      ar: 'العناصر التي سيتم إزالتها',
    },
    itemsThatWillBeAdded: {
      en: 'Items that will be added',
      ar: 'العناصر التي سيتم إضافتها',
    },
    applyChanges: { en: 'Apply Changes', ar: 'تطبيق التغييرات' },
    previewFeature: { en: 'Preview Feature', ar: 'معاينة الميزة' },
    exploreFeatures: { en: 'Explore Features', ar: 'استكشف الميزات' },
    buildAndPrice: { en: 'Build & Price', ar: 'البناء والسعر' },
    features: {
      preview: { en: 'Preview Feature', ar: 'معاينة المميزات' },
      playAll: { en: 'PLAY ALL', ar: 'تشغيل الكل' },
      title: { en: 'Features', ar: 'المميزات' },
    },
    header: {
      localMatches: { en: 'Local\nMatches', ar: 'التطابقات\nالمحلية' },
      matchingVehicles: { en: 'Matching vehicles', ar: 'مركبات مطابقة' },
      configurationsFound: { en: 'Configurations Found', ar: 'تكوينات تم العثور عليها' },
    },
    configPanel: {
      playAll: { en: 'Play All', ar: 'تشغيل الكل' },
      pause: { en: 'Pause', ar: 'إيقاف مؤقت' },
      exteriorColors: { en: 'Exterior Colors', ar: 'ألوان الهيكل الخارجي' },
      interiorColors: { en: 'Interior Colors', ar: 'ألوان الداخلية' },
      roofOptions: { en: 'Roof Options', ar: 'خيارات السقف' },
      interiorOptions: { en: 'Interior Options', ar: 'خيارات الداخلية' },
      exteriorAccessories: { en: 'Exterior Accessories', ar: 'إكسسوارات خارجية' },
      noAdditionalOptions: { en: 'No additional options available for the selected trim.', ar: 'لا توجد خيارات إضافية متاحة للفئة المختارة.' },
      noFilters: { en: 'No filters available.', ar: 'لا توجد فلاتر متاحة.' },
      noVariantsMatch: { en: 'No trims match the selected filters.', ar: 'لا توجد فئات تطابق الفلاتر المختارة.' },
      configurationBreakdown: { en: 'Configuration Breakdown', ar: 'تفاصيل التكوين' },
      yourConfiguration: { en: 'Your Configuration', ar: 'تكوينك' },
      trim: { en: 'Trim', ar: 'الفئة' },
      total: { en: 'Total', ar: 'الإجمالي' },
    },
    conflictDialog: {
      description: { en: 'In order to apply this, the following item(s) will need to be removed due to compatibility constraints. Please review the item(s) below and proceed with your change.', ar: 'لتطبيق هذا، يجب إزالة العناصر التالية بسبب قيود التوافق. يرجى مراجعة العناصر أدناه والمتابعة بتغييرك.' },
    },
    viewport: {
      changeBackground: { en: 'Change Background', ar: 'تغيير الخلفية' },
      selectLanguage: { en: 'Switch Language (عربي)', ar: 'تبديل اللغة (EN)' },
      menu: { en: 'Menu', ar: 'القائمة' },
      exploreFeatures: { en: 'Explore Features', ar: 'استكشف الميزات' },
    },
  },
  dealers: {
    UAE: [
      {
        id: 0,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - القوز 3، المنطقة الصناعية، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الغاندي للسيارات - شارع مذهب محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: 'UAE',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - طريق الاتحاد - صالة عرض , الخبيصي, ديرة, دبي',
        },
      },
      {
        id: 8,
        country: 'UAE',
        value: '',
        dealerId: '334821',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi - Mirdiff City Center',
          ar: 'الغاندي - مردف سيتي سنتر',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: 'UAE',
        dealerId: '323482',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Madinat Zayed, Al Dhafra',
          ar: 'بن حمودة للسيارات - مدينة زايد، الظفرة',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122552',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Al Ain',
          ar: 'بن حمودة للسيارات - العين',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122233',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Abu Dhabi Main',
          ar: 'بن حمودة للسيارات - أبوظبي الرئيسية',
        },
      },
    ],
    KSA: [
      {
        id: 0,
        country: 'KSA',
        value: 'KSA',
        dealerId: '307261',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Aziziyah Madina Road, Jeddah',
          ar: 'الجميح للسيارات - العزيزية طريق المدينة، جدة',
        },
      },
      {
        id: 1,
        country: 'KSA',
        value: 'KSA',
        dealerId: '164034',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Hail City King Abdullah Road. Hail',
          ar: 'الجميح للسيارات - مدينة حائل طريق الملك عبدالله. يشيد',
        },
      },
      {
        id: 2,
        country: 'KSA',
        value: 'KSA',
        dealerId: '164035',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Alshlhoub King Abdullah Road, Al Jouf',
          ar: 'الجميح للسيارات - الشلهوب طريق الملك عبدالله، الجوف',
        },
      },
      {
        id: 3,
        country: 'KSA',
        value: 'KSA',
        dealerId: '307271',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Qassim King Abdulaziz Road, Buraidah',
          ar: 'الجميح للسيارات - القصيم طريق الملك عبد العزيز، بريدة',
        },
      },
      {
        id: 4,
        country: 'KSA',
        value: 'KSA',
        dealerId: '260921',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Jededah Road, Arar',
          ar: 'الجميح للسيارات - طريق الجديدة، عرعر',
        },
      },
    ],
    KW: [
      {
        id: 0,
        country: 'KW',
        value: 'KW',
        dealerId: '122620',
        dealerName: 'Alamana Auto',
        label: {
          en: 'Alamana Auto - Al Rai 4th Ring Road, Al Rai',
          ar: 'الامانة للسيارات - الري الطريق الدائري الرابع، الري',
        },
      },
      {
        id: 1,
        country: 'KW',
        value: 'KW',
        dealerId: '174979',
        dealerName: 'Alamana Auto',
        label: {
          en: 'Alamana Auto - Fahaheel Industrial Area Dabbous Street, Fahaheel',
          ar: 'الامانة للسيارات - الفحيحيل الصناعية شارع الدبوس، الفحيحيل',
        },
      },
    ],
    BHD: [
      {
        id: 0,
        country: 'BHD',
        value: 'BHD',
        dealerId: '122215',
        dealerName: 'National Motor Company W.L.L',
        label: {
          en: 'National Motor Company W.L.L, Al Qarya 475 Shk. Jaber Al Subah Hwy',
          ar: 'الشركة الوطنية للسيارات ذ.م.م، القرية 475 شارع الشيخ. طريق جابر الصباح',
        },
      },
    ],
    IQD: [
      {
        id: 0,
        country: 'IQD',
        value: 'IQD',
        dealerId: '295876',
        dealerName: 'MAC International FZE',
        label: {
          en: 'MAC International FZE, Al-Karada Sadoun Street',
          ar: 'شركة ماك الدولية (منطقة حرة)، شارع الكرادة سعدون',
        },
      },
      {
        id: 1,
        country: 'IQD',
        value: 'IQD',
        dealerId: '295879',
        dealerName: 'MAC International FZE',
        label: {
          en: 'MAC International FZE, Kurdistan District 100 m Street',
          ar: 'ماك إنترناشونال إف زي إي، منطقة كردستان، شارع ١٠٠ متر',
        },
      },
    ],
    LBP: [
      {
        id: 0,
        country: 'LBP',
        value: 'LBP',
        dealerId: '122226',
        dealerName: 'IMPEX',
        label: {
          en: 'IMPEX -  Badaro, Near the National Museum , Beirut',
          ar: 'إمبكس - بدارو، بالقرب من المتحف الوطني، بيروت',
        },
      },
    ],
    QAR: [
      {
        id: 0,
        country: 'QAR',
        value: 'QAR',
        dealerId: '274766',
        dealerName: 'Jaidah',
        label: {
          en: 'Jaidah Square Showroom - Al Matar Street Umm Ghuwallina, Doha',
          ar: 'صالة عرض "جايدة سكوير" - شارع المطار، أم غويلينة، الدوحة',
        },
      },
    ],
    OMR: [
      {
        id: 0,
        country: 'OMR',
        value: 'OMR',
        dealerId: '122219',
        dealerName: 'Oman Trading Establisment',
        label: {
          en: 'Oman Trading Establisment LLC, Ghalla Al Maardh Street',
          ar: 'مؤسسة عمان التجارية ش.م.م، شارع غلا المعرض',
        },
      },
      {
        id: 1,
        country: 'OMR',
        value: 'OMR',
        dealerId: '234217',
        dealerName: 'Oman Trading Establisment',
        label: {
          en: 'Oman Trading Establisment LLC, Al Farq Street',
          ar: 'مؤسسة عمان التجارية ذ.م.م، شارع الفرق',
        },
      },
      {
        id: 2,
        country: 'OMR',
        value: 'OMR',
        dealerId: '260896',
        dealerName: 'Oman Trading Establisment',
        label: {
          en: 'Oman Trading Establisment LLC, Hail Al Shumaliah Street',
          ar: 'مؤسسة عمان التجارية ذ.م.م، شارع حيل الشمالية',
        },
      },
    ],
    JOD: [
      {
        id: 0,
        country: 'JOD',
        value: 'JOD',
        dealerId: '189129',
        dealerName: 'Leading Vehicles',
        label: {
          en: 'The Leading Vehicles Co. Ltd, Al Rawabi',
          ar: 'شركة المركبات الرائدة المحدودة، الروابي',
        },
      },
    ],
    default: [
      {
        id: 0,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - القوز 3، المنطقة الصناعية، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الغاندي للسيارات - شارع مذهب محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: 'UAE',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - طريق الاتحاد - صالة عرض , الخبيصي, ديرة, دبي',
        },
      },
      {
        id: 8,
        country: 'UAE',
        value: '',
        dealerId: '334821',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi - Mirdiff City Center',
          ar: 'الغاندي - مردف سيتي سنتر',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: 'UAE',
        dealerId: '323482',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Madinat Zayed, Al Dhafra',
          ar: 'بن حمودة للسيارات - مدينة زايد، الظفرة',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122552',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Al Ain',
          ar: 'بن حمودة للسيارات - العين',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122233',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Abu Dhabi Main',
          ar: 'بن حمودة للسيارات - أبوظبي الرئيسية',
        },
      },
    ],
  },
  featureFlags: {
    showPriceInPresentation: false,
    showLogo: true,
    showSpecDetailsButton: true,
    useChevroletFont: true,
    showLocalMatches: false,
    showDisclaimerInPresentation: false,
    // Per-item pricing (card / MODEL / SUMMARY price lines, colours in the total)
    // is opt-in; this project keeps the header-only pricing it has always had.
    showPrices: true, // per-item prices on cards + summary breakdown, like Corvette
    showDisplayCarPrice: true,
    showCart: false
  },
  config: {
    // Display currency + USD conversion factor per region, keyed by the
    // `country` URL parameter (local = usd * fx). Prices were re-derived
    // from the AED figure: usd = AED / 3.68, then usd * fx per country.
    regions: {
      KSA: { label: 'KSA', currency: 'SAR', fx: 3.75 },
      UAE: { label: 'UAE', currency: 'AED', fx: 3.68 },
      OMR: { label: 'Oman', currency: 'OMR', fx: 0.39 },
      BHD: { label: 'Bahrain', currency: 'BHD', fx: 0.38 },
      QAR: { label: 'Qatar', currency: 'QAR', fx: 3.64 },
      JOD: { label: 'Jordan', currency: 'JOD', fx: 0.71 },
      KW: { label: 'Kuwait', currency: 'KWD', fx: 0.31 },
      LBP: { label: 'Lebanon', currency: 'USD', fx: 1 }, // GM quotes Lebanon in USD
      IQD: { label: 'Iraq', currency: 'USD', fx: 1 }, // priced in USD, like Lebanon
      default: { label: 'UAE', currency: 'AED', fx: 3.68 },
    },
    // Lead-side model name — 1.0's `config.model`, which is a lead label
    // distinct from 2.0's `config.model` (the models[] key).
    leadModel: 'Equinox',
    // Dial code (prefixed onto mobile_number) and Salesforce lead country
    // code (metadome_country_code), keyed by the `country` URL parameter.
    countryCodes: {
      UAE: {
        code: '+971',
        leadCode: 'AE',
      },
      KSA: {
        code: '+966',
        leadCode: 'SA',
      },
      KW: {
        code: '+965',
        leadCode: 'KW',
      },
      BHD: {
        code: '+973',
        leadCode: 'BH',
      },
      IQD: {
        code: '+964',
        leadCode: 'IQ',
      },
      LBP: {
        code: '+961',
        leadCode: 'LB',
      },
      OMR: {
        code: '+968',
        leadCode: 'OM',
      },
      QAR: {
        code: '+974',
        leadCode: 'QA',
      },
      JOD: {
        code: '+962',
        leadCode: 'JO',
      },
      default: {
        code: '+971',
        leadCode: 'AE',
      },
    },
    model: 'Equinox',
    modelName: 'Equinox',
    pageTitle: 'Equinox Configurator',
    client: 'GM',
    brand: 'chevrolet',
    modelId: 'CHEQUC223_01',
    configurationId: 'l25zrwex1u93',
    externalAnalytics: {
      vehicleType: 'suv',
      provider: 'adobe',
      modelName: 'equinox-ev',
      brand: 'chevrolet'
    },
    presentation: {
      video: 'videos/2362/presentation_video_1.webm',
      loadingImage: 'images/2362/sliderImage.webp',
      transitionImage: 'images/2362/transition_logo.png',
      headerLogoKeepColor: true,
      keyHighlights: [
        {
          index: 0,
          show: true,
          title: {
            en: '',
            ar: ''
          },
          value: {
            en: '213',
            ar: '213'
          },
          unit: 'HP'
        },
        {
          index: 1,
          show: true,
          title: {
            en: '0-100 km*',
            ar: '0إلى100 كم*'
          },
          value: {
            en: '8',
            ar: '8 ثانية'
          },
          unit: 'sec'
        },
        {
          index: 2,
          show: true,
          title: {
            en: 'Range**',
            ar: 'مدى**'
          },
          value: {
            en: '513',
            ar: '513 كم'
          },
          unit: 'km'
        }
      ]
    }
  },
  saveFormConfig: {
    fields: [
      {
        id: 'firstName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelFirstName',
        placeholderKey: 'saveDialog.placeholderFirstName',
      },
      {
        id: 'lastName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelLastName',
        placeholderKey: 'saveDialog.placeholderLastName',
      },
      {
        id: 'email',
        type: 'email',
        required: true,
        labelKey: 'saveDialog.labelEmail',
        placeholderKey: 'saveDialog.placeholderEmail',
        visibility: ['email', 'test-drive', 'callback'],
      },
      {
        id: 'phone',
        type: 'phone',
        required: true,
        labelKey: 'saveDialog.labelMobileNumber',
        placeholderKey: 'saveDialog.placeholderMobileNumber',
        visibility: ['whatsapp', 'test-drive', 'callback'],
      },
      {
        id: 'model',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelModel',
        placeholderKey: 'saveDialog.labelModel',
        optionsSource: 'models',
        visibility: ['test-drive', 'callback'],
      },
      {
        id: 'dealer',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelDealership',
        placeholderKey: 'saveDialog.placeholderSelectDealership',
        optionsSource: 'dealers',
        visibility: [
          'test-drive',
          'callback',
          'whatsapp',
          'email',
        ],
        // 1.0's save forms show the dealership dropdown but do not gate
        // submission on it; the booking forms and Save to WhatsApp
        // require it (GM needs a dealer on WhatsApp leads); Email stays optional.
        requiredFor: [
          'test-drive',
          'callback',
          'whatsapp',
        ],
      },
      {
        id: 'testDriveDate',
        type: 'date',
        required: true,
        labelKey: 'saveDialog.labelTestDriveDate',
        placeholderKey: 'saveDialog.placeholderTestDriveDate',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'testDriveTime',
        type: 'time',
        required: true,
        labelKey: 'saveDialog.labelTestDriveTime',
        placeholderKey: 'saveDialog.placeholderTestDriveTime',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'comments',
        type: 'textarea',
        required: false,
        labelKey: 'saveDialog.labelComments',
        placeholderKey: 'saveDialog.placeholderComments',
        visibility: ['test-drive', 'callback'],
      },
    ],
  },
  externalScript: {
    dev: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-bea0833819b4-development.min.js',
            iframe: true,
            standalone: true,
            async: false
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true
          }
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true
          }
        ]
      },
      body: {
        snippets: [
          {
            snippet: '<script type="text/javascript">_satellite.pageBottom();</script>',
            iframe: true,
            standalone: true,
            async: false
          }
        ]
      }
    },
    staging: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-85b05c76e071.min.js',
            iframe: true,
            standalone: true,
            async: false
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true
          }
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true
          }
        ]
      },
      body: {
        snippets: [
          {
            snippet: '<script type="text/javascript">_satellite.pageBottom();</script>',
            iframe: true,
            standalone: true,
            async: false
          }
        ]
      }
    },
    main: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-85b05c76e071.min.js',
            iframe: true,
            standalone: true,
            async: false
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true
          }
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true
          }
        ]
      },
      body: {
        snippets: [
          {
            snippet: '<script type="text/javascript">_satellite.pageBottom();</script>',
            iframe: true,
            standalone: true,
            async: false
          }
        ]
      }
    }
  },
  filterOptions: [
    {
      id: 'modelyear',
      key: 'modelyear',
      label: {
        en: 'Model Year',
        ar: 'سنة الطراز'
      },
      disabled: false,
      type: 'chip',
      options: [
        {
          id: '2025',
          label: {
            en: '2025',
            ar: '2025'
          },
          value: '2025',
          checked: false
        },
        {
          id: '2026',
          label: {
            en: '2026',
            ar: '2026'
          },
          value: '2026',
          checked: true
        }
      ]
    }
  ],
  styleConfig: {
    cssVariables: {
      '--default-font-family': 'Chevy Sans',
      '--bold-font-family': 'Chevy Sans Medium',
    },
  },
} as unknown as UIMetadataComplete;
